import { useState } from "react";
import { http, resolveImage, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Modal } from "./AdminShared";

const DEFAULT_VARIANTS = [
  { key: "digital", label: "Digital Download", price: 9.99, weight: 0, type: "digital", active: true },
  { key: "4x6", label: "4x6 Print", price: 14.99, weight: 1, type: "print", active: true },
  { key: "11x14", label: "11x14 Print", price: 39.99, weight: 4, type: "print", active: true },
  { key: "16x20", label: "16x20 Print", price: 69.99, weight: 8, type: "print", active: true },
  { key: "16x20-canvas", label: "16x20 Canvas", price: 129.99, weight: 16, type: "canvas", active: true },
  { key: "personal-text", label: "Personal Note Upgrade", price: 19.99, weight: 0, type: "upgrade", active: true },
];

export default function ArtworkModal({ art, onClose, onSaved }) {
  const isNew = !art;
  const [form, setForm] = useState(art ? { ...art } : {
    title: "", story: "", short_description: "", main_image: "", additional_images: [],
    digital_file_url: "", audio_url: "", youtube_url: "", tags: [],
    status: "active", featured: false, subscription_eligible: true, qr_eligible: true,
    subscriber_only: false,
    category: "Originals", collection_number: null,
    variants: DEFAULT_VARIANTS, seo_title: "", seo_description: "",
  });
  const [saving, setSaving] = useState(false);

  const uploadImage = async (file) => new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await http.post("/upload/image", { data_uri: reader.result, filename: file.name });
        res(data.url);
      } catch (e) { rej(e); }
    };
    reader.readAsDataURL(file);
  });

  const uploadAudio = async (file) => new Promise((res, rej) => {
    if (file.size > 10 * 1024 * 1024) { rej(new Error("Audio must be 10 MB or smaller")); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await http.post("/upload/audio", { data_uri: reader.result, filename: file.name });
        res(data.url);
      } catch (e) { rej(e); }
    };
    reader.readAsDataURL(file);
  });

  const onPickMain = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const url = await uploadImage(f); setForm({...form, main_image: url}); toast.success("Uploaded."); }
    catch { toast.error("Upload failed."); }
  };

  const onPickAudio = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const url = await uploadAudio(f); setForm({...form, audio_url: url}); toast.success("Audio uploaded."); }
    catch (err) { toast.error(err?.message || "Audio upload failed."); }
  };

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const body = { ...form };
      if (body.collection_number) body.collection_number = parseInt(body.collection_number);
      if (isNew) await http.post("/artworks", body);
      else await http.put(`/artworks/${art.id}`, body);
      toast.success("Saved."); onSaved();
    } catch (err) { toast.error("Save failed: " + (err?.response?.data?.detail || "")); }
    finally { setSaving(false); }
  };

  const updVariant = (i, patch) => {
    const v = [...form.variants]; v[i] = { ...v[i], ...patch };
    setForm({...form, variants: v});
  };

  return (
    <Modal title={isNew ? "New Artwork" : `Edit · ${form.title}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <input required placeholder="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="stc-input" data-testid="art-title-input"/>
        <div className="grid grid-cols-3 gap-3">
          <input type="number" placeholder="Collection #" value={form.collection_number || ""} onChange={e => setForm({...form, collection_number: e.target.value})} className="stc-input"/>
          <input placeholder="Category" value={form.category || ""} onChange={e => setForm({...form, category: e.target.value})} className="stc-input"/>
          <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="stc-input">
            <option value="active">Active</option><option value="draft">Draft</option><option value="archived">Archived</option>
          </select>
        </div>
        <textarea placeholder="Short description" rows={2} value={form.short_description} onChange={e => setForm({...form, short_description: e.target.value})} className="stc-input font-body"/>
        <textarea placeholder="Full story" rows={5} value={form.story} onChange={e => setForm({...form, story: e.target.value})} className="stc-input font-body"/>
        <div>
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-2">Main Image</div>
          <div className="flex gap-3 items-center">
            <input placeholder="Paste URL" value={form.main_image} onChange={e => setForm({...form, main_image: e.target.value})} className="stc-input flex-1" data-testid="art-image-url"/>
            <label className="btn-outline cursor-pointer">Upload<input type="file" accept="image/*" className="hidden" onChange={onPickMain}/></label>
          </div>
          {form.main_image && <img src={resolveImage(form.main_image)} alt="" className="mt-3 w-40 h-40 object-cover border border-white/10"/>}
        </div>
        <div>
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-2">Digital File (delivered to digital buyers)</div>
          <input placeholder="High-res file URL (defaults to main image if blank)" value={form.digital_file_url || ""} onChange={e => setForm({...form, digital_file_url: e.target.value})} className="stc-input" data-testid="art-digital-file-url"/>
        </div>
        <div>
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-2">Story Tags (themes shown on artwork page — comma separated)</div>
          <input
            placeholder="Hope, Strength, Resilience, Growth, Faith, Perspective"
            value={(form.tags || []).join(", ")}
            onChange={e => setForm({...form, tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})}
            className="stc-input"
            data-testid="art-tags-input"
          />
          <p className="font-body text-[11px] text-[#8a857d] mt-1">Defaults to a tasteful starter set if blank. These appear under "What this piece represents" in the Story Experience.</p>
        </div>
        <div>
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-2">Voice Recording (MP3) — for QR Story-mode pages</div>
          <div className="flex gap-3 items-center">
            <input placeholder="Paste audio URL or upload below" value={form.audio_url || ""} onChange={e => setForm({...form, audio_url: e.target.value})} className="stc-input flex-1" data-testid="art-audio-url"/>
            <label className="btn-outline cursor-pointer" data-testid="art-audio-upload-btn">Upload MP3<input type="file" accept="audio/mpeg,audio/mp3,audio/*" className="hidden" onChange={onPickAudio}/></label>
            {form.audio_url && (
              <button type="button" onClick={() => setForm({...form, audio_url: ""})} className="btn-outline border-red-700 text-red-400" data-testid="art-audio-clear-btn"><Trash2 size={12}/></button>
            )}
          </div>
          {form.audio_url && (
            <audio controls preload="none" src={form.audio_url.startsWith("/api/") ? `${API_BASE}${form.audio_url}` : form.audio_url} className="mt-3 w-full" data-testid="art-audio-preview"/>
          )}
          <p className="font-body text-[11px] text-[#8a857d] mt-2">Max 10 MB. Plays on story-mode QR landing pages so collectors hear the piece in your voice before deciding to buy.</p>
        </div>
        <div>
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-2">YouTube Video URL</div>
          <div className="flex gap-3 items-center">
            <input placeholder="https://youtube.com/watch?v=..." value={form.youtube_url || ""} onChange={e => setForm({...form, youtube_url: e.target.value})} className="stc-input flex-1" data-testid="art-youtube-url"/>
            {form.youtube_url && (
              <button type="button" onClick={() => setForm({...form, youtube_url: ""})} className="btn-outline border-red-700 text-red-400" data-testid="art-youtube-clear-btn"><Trash2 size={12}/></button>
            )}
          </div>
          <p className="font-body text-[11px] text-[#8a857d] mt-2">Paste a YouTube link (e.g. watch?v=... or youtu.be/...) to show an embedded video on the artwork details page.</p>
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2 text-[#F4F1EA]"><input type="checkbox" className="accent-[#C9A055]" checked={form.featured} onChange={e => setForm({...form, featured: e.target.checked})}/> Featured</label>
          <label className="flex items-center gap-2 text-[#F4F1EA]"><input type="checkbox" className="accent-[#C9A055]" checked={form.subscription_eligible} onChange={e => setForm({...form, subscription_eligible: e.target.checked})}/> Subscription eligible</label>
          <label className="flex items-center gap-2 text-[#F4F1EA]"><input type="checkbox" className="accent-[#C9A055]" checked={form.qr_eligible} onChange={e => setForm({...form, qr_eligible: e.target.checked})}/> QR eligible</label>
          <label className="flex items-center gap-2 text-[#C9A055]"><input type="checkbox" className="accent-[#C9A055]" checked={form.subscriber_only || false} onChange={e => setForm({...form, subscriber_only: e.target.checked})}/> Members only</label>
        </div>

        {/* Per-artwork Special */}
        <div className="border border-[#C9A055]/25 bg-[#0e0c08] p-4" data-testid="art-special-block">
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">Special / Sale</div>
          <div className="grid sm:grid-cols-2 gap-3 items-end">
            <label className="flex items-center gap-2 text-[#F4F1EA]">
              <input type="checkbox" className="accent-[#C9A055]" data-testid="art-special-active"
                checked={!!(form.special && form.special.active)}
                onChange={e => setForm({ ...form, special: { ...(form.special || {}), active: e.target.checked } })}/>
              Mark as Special
            </label>
            <label className="block"><span className="text-[10px] tracking-[0.22em] uppercase text-[#9a9690]">Badge label</span>
              <input data-testid="art-special-label" className="stc-input mt-1" placeholder="LIMITED · 25% OFF"
                value={(form.special && form.special.label) || ""}
                onChange={e => setForm({ ...form, special: { ...(form.special || {}), label: e.target.value } })}/>
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 items-end mt-3">
            <label className="block"><span className="text-[10px] tracking-[0.22em] uppercase text-[#C9A055]">% off (applies to all variants)</span>
              <input data-testid="art-special-pct-off" type="number" step="1" min="0" max="90" className="stc-input mt-1 font-mono"
                placeholder="e.g. 25"
                value={(form.special && form.special.pct_off) || ""}
                onChange={e => setForm({ ...form, special: { ...(form.special || {}), pct_off: e.target.value ? parseFloat(e.target.value) : null } })}/>
            </label>
            <label className="block"><span className="text-[10px] tracking-[0.22em] uppercase text-[#9a9690]">Flat sale price (overrides ALL variants)</span>
              <input data-testid="art-special-sale-price" type="number" step="0.01" className="stc-input mt-1 font-mono"
                placeholder="Use ONLY for single-variant artworks"
                value={(form.special && form.special.sale_price) || ""}
                onChange={e => setForm({ ...form, special: { ...(form.special || {}), sale_price: e.target.value ? parseFloat(e.target.value) : null } })}/>
            </label>
          </div>
          <p className="font-body text-[11px] text-[#8a857d] mt-2">Use <strong className="text-[#C9A055]">% off</strong> for prints with multiple variants (each variant discounts by the same %). Use <strong className="text-[#C9A055]">flat sale price</strong> only if every variant should sell for that single number.</p>
        </div>
        <div>
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-2">Variants</div>
          <div className="space-y-2">
            {form.variants.map((v, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center text-sm">
                <input value={v.label} onChange={e => updVariant(i, { label: e.target.value })} className="stc-input col-span-4"/>
                <input value={v.key} onChange={e => updVariant(i, { key: e.target.value })} className="stc-input col-span-2 font-mono"/>
                <input type="number" step="0.01" value={v.price} onChange={e => updVariant(i, { price: parseFloat(e.target.value) })} className="stc-input col-span-2"/>
                <input type="number" step="0.1" placeholder="oz" value={v.weight} onChange={e => updVariant(i, { weight: parseFloat(e.target.value) })} className="stc-input col-span-2"/>
                <select value={v.type} onChange={e => updVariant(i, { type: e.target.value })} className="stc-input col-span-2">{["digital","print","canvas","upgrade"].map(t => <option key={t}>{t}</option>)}</select>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-3 border-t border-white/10">
          <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          <button disabled={saving} className="btn-gold" data-testid="art-save-btn">{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </Modal>
  );
}
